import { Alert, Button, Card, Collapse, Empty, Space, Tag, theme } from 'antd';
import { useMemo, useState } from 'react';
import { buildCodexTranscript, type CodexEntry } from './codexTranscript';
import { JsonLineView } from './JsonLineView';
import { type PreviewSnapshot, parseJsonLine } from './preview';

const PAGE_SIZE = 20;
const MAX_DISPLAY_CHARS = 64 * 1024;
const statusLabels = {
  unresolved: '未加载到结果',
  returned: '已记录结果',
  succeeded: '执行完成',
  failed: '执行失败',
};

function TextContent({ text }: { text: string }) {
  return (
    <>
      <pre className="m-0 max-h-80 overflow-auto whitespace-pre-wrap break-all text-sm">
        {text.slice(0, MAX_DISPLAY_CHARS)}
      </pre>
      {text.length > MAX_DISPLAY_CHARS && (
        <p className="mt-2 text-xs text-muted-foreground">
          内容较长，此处展示前 64 Ki 字符；完整记录可在原文中查看。
        </p>
      )}
    </>
  );
}

function ToolDetails({ entry }: { entry: CodexEntry }) {
  const parsedInput = useMemo(
    () =>
      parseJsonLine({
        number: entry.line,
        text: entry.input ?? '',
        complete: true,
      }),
    [entry.line, entry.input],
  );
  return (
    <div className="space-y-3">
      {entry.callId && (
        <div className="break-all text-xs text-muted-foreground">
          调用 ID：{entry.callId}
        </div>
      )}
      {!entry.hasCall && (
        <Alert type="warning" title="仅加载到工具结果，未找到对应调用记录" />
      )}
      <div>
        <p className="mb-2 text-xs font-semibold">调用参数 / 输入</p>
        {parsedInput ? (
          <JsonLineView value={parsedInput.value} />
        ) : (
          <TextContent text={entry.input || '此记录未包含调用参数。'} />
        )}
      </div>
      <div>
        <p className="mb-2 text-xs font-semibold">工具结果</p>
        <TextContent
          text={
            entry.output !== undefined
              ? entry.output || '工具返回了空结果。'
              : '当前已加载内容中尚未找到工具结果。'
          }
        />
      </div>
      <Space wrap>
        {entry.exitCode !== undefined && <Tag>退出码 {entry.exitCode}</Tag>}
        {entry.durationMs !== undefined && (
          <Tag>耗时 {(entry.durationMs / 1000).toFixed(2)} 秒</Tag>
        )}
      </Space>
    </div>
  );
}

function EntryView({ entry }: { entry: CodexEntry }) {
  const { token } = theme.useToken();
  const inputSummary = useMemo(() => {
    const parsed = parseJsonLine({
      number: entry.line,
      text: entry.input ?? '',
      complete: true,
    });
    const value = parsed?.value;
    let summary = entry.input ?? '';
    if (Array.isArray(value) && value.every((item) => typeof item === 'string'))
      summary = value.join(' ');
    else if (value !== null && typeof value === 'object') {
      const argumentsValue = value as Record<string, unknown>;
      for (const key of [
        'cmd',
        'command',
        'file_path',
        'path',
        'query',
        'code',
      ]) {
        if (typeof argumentsValue[key] === 'string') {
          summary = argumentsValue[key];
          break;
        }
      }
    }
    return summary.split('\n', 1)[0].slice(0, 180);
  }, [entry.input, entry.line]);
  const time = new Date(entry.timestamp);
  const source = `原文行 ${entry.sourceLines.join(', ')}`;
  if (entry.kind === 'tool') {
    return (
      <Card size="small">
        <Collapse
          ghost
          items={[
            {
              key: entry.id,
              label: (
                <Space wrap>
                  <strong>工具 · {entry.title}</strong>
                  <Tag
                    color={
                      entry.status === 'failed'
                        ? 'error'
                        : entry.status === 'succeeded'
                          ? 'success'
                          : 'default'
                    }
                  >
                    {statusLabels[entry.status ?? 'unresolved']}
                  </Tag>
                </Space>
              ),
              children: <ToolDetails entry={entry} />,
            },
          ]}
        />
        {inputSummary && (
          <div className="mb-2 truncate font-mono text-xs" title={inputSummary}>
            {inputSummary}
          </div>
        )}
        <div className="text-xs text-muted-foreground">{source}</div>
      </Card>
    );
  }
  if (
    entry.kind === 'reasoning' ||
    entry.role === 'system' ||
    entry.kind === 'event'
  ) {
    return (
      <Collapse
        size="small"
        items={[
          {
            key: entry.id,
            label: (
              <Space>
                <span>{entry.title}</span>
                <span className="text-xs text-muted-foreground">{source}</span>
              </Space>
            ),
            children: <TextContent text={entry.text || '记录未包含正文。'} />,
          },
        ]}
      />
    );
  }
  return (
    <Card
      size="small"
      style={{
        background:
          entry.role === 'user' ? token.colorPrimaryBg : token.colorBgContainer,
      }}
    >
      <Space wrap className="mb-3">
        <Tag color={entry.role === 'user' ? 'blue' : 'green'}>
          {entry.title}
        </Tag>
        {entry.phase && (
          <Tag>
            {entry.phase === 'final_answer'
              ? '最终回复'
              : entry.phase === 'commentary'
                ? '进展'
                : entry.phase}
          </Tag>
        )}
        {!Number.isNaN(time.getTime()) && (
          <span className="text-xs text-muted-foreground">
            {time.toLocaleTimeString()}
          </span>
        )}
        <span className="text-xs text-muted-foreground">{source}</span>
      </Space>
      <TextContent text={entry.text} />
    </Card>
  );
}

export function CodexTranscriptView({
  snapshot,
}: {
  snapshot: PreviewSnapshot;
}) {
  const transcript = useMemo(
    () => buildCodexTranscript(snapshot.lines),
    [snapshot.lines],
  );
  const [page, setPage] = useState(1);
  const messages = transcript.entries.filter(
    (entry) => entry.kind === 'message' && entry.role !== 'system',
  ).length;
  const tools = transcript.entries.filter(
    (entry) => entry.kind === 'tool',
  ).length;
  const entries = transcript.entries.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );
  return (
    <div className="space-y-3">
      <Space wrap>
        <Tag>Codex 会话</Tag>
        {transcript.model && <Tag>{transcript.model}</Tag>}
        <span className="text-sm text-muted-foreground">
          {messages} 条消息 · {tools} 次工具调用
        </span>
      </Space>
      {transcript.unreadableLines > 0 && (
        <Alert
          type="info"
          showIcon
          title={`${transcript.unreadableLines} 条记录尚未解析为会话内容`}
          description="可能是未加载完整、格式未知或记录过大；可切换原文查看。"
        />
      )}
      {entries.length ? (
        entries.map((entry) => <EntryView key={entry.id} entry={entry} />)
      ) : (
        <Empty description="尚未识别到 Codex 会话内容，可切换原文查看" />
      )}
      {transcript.entries.length > PAGE_SIZE && (
        <Space>
          <Button disabled={page === 1} onClick={() => setPage(page - 1)}>
            上一段会话
          </Button>
          <span>第 {page} 页</span>
          <Button
            disabled={page * PAGE_SIZE >= transcript.entries.length}
            onClick={() => setPage(page + 1)}
          >
            下一段会话
          </Button>
        </Space>
      )}
    </div>
  );
}
